/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { IKeyValueAny } from "../../Administrations/types";
import { validateInput } from "./helpers";
import { useStoreDispatch } from "../../../store/hooks";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";

interface NumberFieldProps {
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement | HTMLDivElement> | undefined;
  onKeyUp?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement | HTMLDivElement> | undefined;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
  fullWidth?: boolean | undefined;
  id?: string | undefined;
  label: string;
  name: string;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: React.ReactNode;
  value?: any | undefined;
  inputMode?: "numeric" | "decimal" | "number" | undefined;
  max?: number;
  min?: number;
}
const useStyles = makeStyles({
  customNumberField: {
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    '& input[type="number"]': {
      "-moz-appearance": "textfield",
    },
  },
});
const NumberField: React.FC<NumberFieldProps> = ({
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  onKeyUp,
  fullWidth,
  id,
  label,
  name,
  disabled,
  required,
  error,
  helperText,
  value,
  inputMode = "numeric",
  max,
  min,
}) => {
  const dispatch = useStoreDispatch();
  const classes = useStyles();
  const regxType = {
    number: /[^0-9-]*$/g, // /\D+/g, // /^[0-9]*$/g       // only accept the positive number like 0,1,2,3 .....n
    decimal: /[^0-9.]/g, // only accept the positive decimal number
    numeric: /[^0-9.-]/g, // only accept the positive and negative with decimal number
  } as { [key: string]: RegExp };

  const errorMsg = useSelector(errorMsgSelector);
  const onBlurHandler = (event: any) => {
    const { name } = event.target;
    const { valid, validErrorMessage } = validateInput(event);
    if (!valid) {
      dispatch(setErrorMsg({ ...errorMsg, [name]: validErrorMessage }));
    }
    onBlur && onBlur(event);
  };

  const handleInput = (event: any) => {
    const { name } = event.target;
    const errmsg: IKeyValueAny = Object.fromEntries(Object.entries(errorMsg).filter(([key, value]) => key !== name)) || {};
    dispatch(setErrorMsg(errmsg));
    event.target.setCustomValidity("");
  };

  return (
    <TextField
      onWheel={(event: any) => event.target.blur()}
      className={classes.customNumberField}
      fullWidth={fullWidth}
      id={id}
      label={label}
      name={name}
      type="number"
      disabled={disabled}
      required={required}
      onChange={onChange}
      onBlur={onBlurHandler}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onInput={handleInput}
      error={!!errorMsg?.[name] || error}
      helperText={errorMsg?.[name] || helperText}
      value={String(value)?.replace(regxType[inputMode], "")} //.replace(/[eE\-+]/g, "")
      InputProps={{
        inputProps: {
          inputMode: "numeric",
          max: max,
          min: min,
        },
      }}
    />
  );
};
export default NumberField;
